// V2-GAC embed config + token acquisition.
//
// PRODUCTION PATH (default): the token is minted SERVER-SIDE by HotService (`POST gac/v2/token`) and handed
// to the browser already signed — the signing secret never reaches the client, so tokens can't be forged.
// See fetchGacToken() below.
//
// DEV FALLBACK (opt-in only): signDevGacToken() mints the HS256 token IN THE BROWSER with a shared secret.
// That secret is exposed to anyone who loads the page, so it is forgeable and must NEVER be used in
// production. It exists only so the full postMessage handshake can be exercised locally without a
// token-minting backend. Enable it explicitly with REACT_APP_V2GAC_DEV_SIGN=true.

import BaseAPI from "../service/BaseAPI";

// The embedded HotUtilsV2-GAC app's origin, per environment. Override with REACT_APP_V2GAC_URL.
export function getV2GacUrl(): string {
  const override = process.env.REACT_APP_V2GAC_URL;
  if (override) return override;

  const host = window.location.host;
  // ⚠️ PROD + STAGING point at the container app's own FQDN, not <app>.hotutils.com. Those subdomains
  // were an aspirational convention copied from the first GAC embed and were NEVER provisioned - all twelve
  // failed to resolve (verified 2026-08-12). There is no staging container app either, so staging uses the
  // PROD one, which already accepts staging.hotutils.com as a framer via its PARENT_ORIGIN allowlist.
  // 🔴 The tab is still gated OFF outside development by isV2GacEnabled() - fixing the URL first means the
  // wiring is correct with nothing user-visible to roll back.
  if (host === 'hotutils.com' || host === 'www.hotutils.com') return 'https://hotutils-gac-prod.kindflower-9339c968.southcentralus.azurecontainerapps.io';
  if (host.includes('localhost')) return 'http://localhost:5173'; // local GAC dev server
  // Deployed development site -> the deployed dev GAC container app.
  if (host.startsWith('development')) return 'https://hotutils-gac-dev.braveplant-56045ab0.southcentralus.azurecontainerapps.io';
  return 'https://hotutils-gac-prod.kindflower-9339c968.southcentralus.azurecontainerapps.io'; // staging (deploy a staging GAC + update when it exists)
}

export function getV2GacOrigin(): string {
  return new URL(getV2GacUrl()).origin;
}

/**
 * The V2-GAC tab is DEV-ONLY for now — only surface it (tab + route) on the Development site and in local dev;
 * keep it suppressed on staging/production until it's ready. Mirrors the host-based env detection in
 * utils/api-key.ts (development.hotutils.com / localhost = the Development stack).
 */
export function isV2GacEnabled(): boolean {
  const host = window.location.host;
  return host === 'development.hotutils.com' || host.startsWith('localhost');
}

/** True when the dev-only in-browser signer should be used instead of the server endpoint (local dev only). */
export function useDevSigner(): boolean {
  return process.env.REACT_APP_V2GAC_DEV_SIGN === 'true';
}

interface GacV2TokenResponse {
  token?: string;
  issuedAtUnix?: number;
  expiresAtUnix?: number;
}

/**
 * PRODUCTION PATH: ask HotService to mint the embed token. The current session (cookie/APIUserId) authenticates
 * the call; the server derives playerId from the session and signs the token — the browser only receives the
 * finished JWT. `sessionId` is passed through so the server binds it into the token for the GAC BFF.
 */
export async function fetchGacToken(sessionId?: string): Promise<string> {
  const resp: GacV2TokenResponse = await BaseAPI.postToApi('gac/v2/token', { sessionId: sessionId ?? null });
  if (!resp || !resp.token) throw new Error('GAC token endpoint returned no token');
  return resp.token;
}

function base64Url(bytes: Uint8Array): string {
  let bin = '';
  bytes.forEach((b) => (bin += String.fromCharCode(b)));
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function base64UrlStr(s: string): string {
  return base64Url(new TextEncoder().encode(s));
}

export interface V2GacTokenClaims {
  sub: string;
  sessionId?: string;
  allyCode?: string;
  playerId?: string;
}

// The shared HS256 secret the GAC BFF verifies with (PARENT_TOKEN_SECRET). Dev default matches the GAC's
// .env.example. Override with REACT_APP_V2GAC_DEV_SECRET. Only used by the dev fallback signer.
function getDevSecret(): string {
  return process.env.REACT_APP_V2GAC_DEV_SECRET || 'dev-shared-secret-change-me';
}

/**
 * DEV-ONLY FALLBACK: mint an HS256 JWT the GAC BFF will verify (see the warning at the top of this file).
 * Gated behind useDevSigner() — never runs unless REACT_APP_V2GAC_DEV_SIGN=true.
 */
export async function signDevGacToken(claims: V2GacTokenClaims): Promise<string> {
  const now = Math.floor(Date.now() / 1000);
  const payload = { ...claims, iss: 'hotutils', aud: 'hotutils-gac', iat: now, exp: now + 60 * 60 }; // 1h
  const header = { alg: 'HS256', typ: 'JWT' };
  const data = `${base64UrlStr(JSON.stringify(header))}.${base64UrlStr(JSON.stringify(payload))}`;

  const key = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(getDevSecret()),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  const sig = await crypto.subtle.sign('HMAC', key, new TextEncoder().encode(data));
  return `${data}.${base64Url(new Uint8Array(sig))}`;
}
